import { useState, useEffect, useCallback, useRef } from 'react';
import { ActivityData } from '../types';
import { getApiUrl, type ActiveMind } from '../services/api';

const POLL_INTERVAL = 30_000; // 30s — activity doesn't change as fast as signals

export function useActivity(token: string | null, mind: ActiveMind = 'hannah') {
  const [data, setData] = useState<ActivityData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nudgeLoading, setNudgeLoading] = useState(false);
  const [nudgeMessage, setNudgeMessage] = useState<string | null>(null);
  const fetchingRef = useRef(false);

  const fetchActivity = useCallback(async () => {
    if (!token || fetchingRef.current) return;
    fetchingRef.current = true;

    try {
      const response = await fetch(`${getApiUrl(mind)}/activity`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Activity API error: ${response.status}`);
      }

      const result: ActivityData = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch activity');
    } finally {
      setIsLoading(false);
      fetchingRef.current = false;
    }
  }, [token, mind]);

  useEffect(() => {
    if (!token) return;
    fetchActivity();
    const interval = setInterval(fetchActivity, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [token, fetchActivity]);

  const nudge = useCallback(async () => {
    if (!token || nudgeLoading) return;
    setNudgeLoading(true);
    setNudgeMessage(null);

    try {
      const response = await fetch(`${getApiUrl(mind)}/nudge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Nudge failed: ${response.status}`);
      }

      const result = await response.json() as { message: string };
      setNudgeMessage(result.message);
      
      // Re-fetch activity after nudge
      setTimeout(fetchActivity, 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send nudge');
    } finally {
      setNudgeLoading(false);
    }
  }, [token, mind, nudgeLoading, fetchActivity]);

  return {
    data,
    isLoading,
    error,
    refresh: fetchActivity,
    nudge,
    nudgeLoading,
    nudgeMessage,
  };
}
